import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  useTheme
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { PlayArrow } from '@mui/icons-material';
import { getRolesByRegion } from '../../data/roles';
import { formatCurrency } from '../../utils/formatCurrency';
import { useCurrency } from '../../contexts/CurrencyContext';
import AttendeePicker from '../../components/AttendeePicker';

export default function LiveMeetingForm({ attendees, onAttendeesChange, onStart }) {
  const theme = useTheme();
  const { currency, region } = useCurrency();
  const roles = getRolesByRegion(region);
  const [meetingName, setMeetingName] = useState('');
  const handleAttendeeChange = (roleKey, value) => {
    onAttendeesChange({
      ...attendees,
      [roleKey]: value,
    });
  };

  const totalAttendees = Object.values(attendees).reduce((sum, count) => sum + count, 0);
  
  const hourlyRate = roles.reduce((total, role) => {
    const count = attendees[role.key] || 0;
    return total + (role.hourlyRate * count);
  }, 0);

  const canStart = totalAttendees > 0;

  const handleStart = () => {
    if (canStart) {
      onStart(attendees, meetingName.trim() || null);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          mb: 2,
          textAlign: 'center',
          fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
          fontWeight: 700,
        }}
      >
        Live Meeting Timer
      </Typography>

      <Typography
        variant="subtitle1"
        align="center"
        sx={{
          mb: 3,
          color: 'text.secondary',
          fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
        }}
      >
        Select attendees and start your meeting to track live costs
      </Typography>

      <Grid container rowSpacing={3}>
        {/* Section: Meeting Name */}
        <Grid size={{ xs: 12 }}>
          <Box sx={{ p: 2, backgroundColor: 'action.hover', borderRadius: 2 }}>
            <TextField
              fullWidth
              label="Meeting Name (Optional)"
              placeholder="e.g., Weekly Sprint Planning, Product Review..."
              value={meetingName}
              onChange={(e) => setMeetingName(e.target.value)}
              sx={{ mb: 0 }}
              helperText="Give your meeting a name to track it in the summary"
            />
          </Box>
        </Grid>

        {/* Section: Attendees */}
        <Grid size={{ xs: 12 }}>
          <Box sx={{ p: 2, backgroundColor: 'action.hover', borderRadius: 2 }}>
            <Grid container columnSpacing={2} rowSpacing={2}>
              {roles.map((role) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={role.key}>
                  <AttendeePicker
                    label={role.label}
                    value={attendees[role.key] || 0}
                    onChange={(value) => handleAttendeeChange(role.key, value)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>

        {/* Meeting Summary */}
        {totalAttendees > 0 && (
          <Grid size={{ xs: 12 }}>
            <Box 
              sx={{ 
                p: 2, 
                borderRadius: 2,
                background: theme.palette.mode === 'dark' 
                  ? 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)'
                  : 'linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(25, 118, 210, 0.03) 100%)',
                border: theme.palette.mode === 'dark' 
                  ? '1px solid rgba(255,255,255,0.15)'
                  : '1px solid rgba(25, 118, 210, 0.12)',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Typography 
                variant="h6" 
                sx={{
                  mb: 2,
                  fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                  fontWeight: 700,
                  color: theme.palette.mode === 'dark' ? '#ffffff' : 'primary.main',
                }}
              >
                Ready to Start
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontFamily: 'monospace',
                      fontWeight: 'bold',
                      color: theme.palette.mode === 'dark' ? '#ffffff' : 'primary.main',
                      mb: 0.5,
                    }}
                  >
                    {totalAttendees}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                      fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                      fontWeight: 600,
                    }}
                  >
                    {totalAttendees === 1 ? 'Person' : 'People'}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontFamily: 'monospace',
                      fontWeight: 'bold',
                      color: theme.palette.mode === 'dark' ? '#69db7c' : 'secondary.main',
                      mb: 0.5,
                    }}
                  >
                    {formatCurrency(hourlyRate, currency)}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                      fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                      fontWeight: 600,
                    }}
                  >
                    Per Hour
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        )}

        {/* Start Button */}
        <Grid size={{ xs: 12 }}>
          <Box display="flex" justifyContent="center" mt={1}>
            <Button
              variant="contained"
              color="customGreen"
              size="large"
              onClick={handleStart}
              disabled={!canStart}
              startIcon={<PlayArrow />}
              sx={{ 
                px: 5, 
                py: 2, 
                fontSize: '1.25rem', 
                fontFamily: '"Comic Neue", "Comic Sans MS", cursive', 
                fontWeight: 900 
              }}
            >
              Start Meeting
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
}